import type { WhoAmI } from "../../lib/session";
import { effectiveRoles } from "../../lib/session";

export type ContentKind = "notice" | "portal";
export type ContentStatus =
  | "DRAFT"
  | "PENDING_PRIVACY_REVIEW"
  | "PENDING_RELEASE"
  | "RELEASED"
  | "SCHEDULED"
  | "SUPERSEDED"
  | "PUBLISHED"
  | "DISCARDED"
  | "WITHDRAWN";
export type ContentRecord = {
  id: string;
  status: ContentStatus;
  version: number;
  title: string;
  organization: { id: string; name: string };
  authoredBy: { accountId: string; displayName: string };
  updatedAt: string;
  body: string | string[];
  releasedNotificationStatus?: "PUBLISHED" | "WITHDRAWN" | null;
  releasedNotificationVersion?: number | null;
  category?: string;
  publisherLabel?: string;
  requiresAcknowledgement?: boolean;
  audienceMode?: "ORGANIZATION_SUBTREE" | "EXPLICIT_ORGANIZATIONS";
  audienceOrganizationIds?: string[];
  audienceCount?: number;
  sourceType?: string;
  sourceRef?: string;
  publicRef?: string;
  revisionNo?: number;
  correctsContentId?: string;
  supersededByContentId?: string;
  scheduledPublishAt?: string;
  subjectConsentRef?: string;
  channelKey?: string;
  channelTitle?: string;
  genre?: string;
  digest?: string;
  displayDate?: string;
  themes?: string[];
  publicSource?: string;
  privacyReviewedBy?: { displayName: string };
  releasedBy?: { displayName: string };
  publishedAt?: string;
  withdrawnReason?: string;
  attachments?: { name: string }[];
  /** 公开后的封面：服务端按公开规则给出（第一张 ACTIVE 的 JPEG/PNG 附件）。 */
  cover?: { id: string; name: string };
  timeline?: Array<{
    sequence: number;
    fromStatus?: ContentStatus;
    toStatus: ContentStatus;
    occurredAt: string;
    note?: string;
  }>;
};
export type OrgNode = { id: string; name: string; children?: OrgNode[] };
export const flattenOrgs = (nodes: OrgNode[]): OrgNode[] =>
  nodes.flatMap((node) => [node, ...flattenOrgs(node.children ?? [])]);
export const subtree = (nodes: OrgNode[], id: string): OrgNode[] => {
  const root = flattenOrgs(nodes).find((node) => node.id === id);
  return root ? flattenOrgs([root]) : [];
};
// 只展开内容起草职责的范围，不能把其他职责的组织并入起草权。
export function writableOrgs(nodes: OrgNode[], whoami: WhoAmI | null) {
  const ids = new Set(
    effectiveRoles(whoami)
      .filter((role) => role.roleCode === "CONTENT_AUTHOR")
      .flatMap((role) =>
        role.scopeOrganizationIds.flatMap((id) =>
          role.scopeMode === "SUBTREE_DYNAMIC" ? subtree(nodes, id).map((node) => node.id) : [id]
        )
      )
  );
  return flattenOrgs(nodes).filter((node) => ids.has(node.id));
}
export const STATUS_LABEL: Record<ContentStatus, string> = {
  DRAFT: "起草中",
  PENDING_PRIVACY_REVIEW: "待保密审查",
  PENDING_RELEASE: "待放行",
  RELEASED: "已发布",
  PUBLISHED: "已公开",
  SCHEDULED: "定时待公开",
  SUPERSEDED: "已被纠错取代",
  DISCARDED: "已作废",
  WITHDRAWN: "已撤下"
};
export function displayStatus(record: ContentRecord): ContentStatus {
  return record.status === "RELEASED" && record.releasedNotificationStatus === "WITHDRAWN" ? "WITHDRAWN" : record.status;
}
export function statusLabel(kind: ContentKind, record: ContentRecord): string {
  const status = displayStatus(record);
  return kind === "notice" && status === "WITHDRAWN" ? "已撤回" : STATUS_LABEL[status];
}
export function statusTone(status: ContentStatus) {
  return status === "RELEASED" || status === "PUBLISHED"
    ? ("success" as const)
    : status.startsWith("PENDING")
      ? ("warning" as const)
      : ("neutral" as const);
}
export const CONFIG = {
  notice: {
    title: "通知发布",
    noun: "通知",
    endpoint: "/api/v1/notification-drafts",
    update: "content",
    submit: "submit-for-release",
    submitLabel: "送审",
    description: "写好一份通知，让该收到的人清楚下一步。",
    statuses: ["DRAFT", "PENDING_RELEASE", "RELEASED", "DISCARDED"] as ContentStatus[]
  },
  portal: {
    title: "门户内容",
    noun: "内容",
    endpoint: "/api/v1/portal-contents",
    update: "update",
    submit: "submit-privacy-review",
    submitLabel: "送保密审查",
    description: "从一份草稿，到经过审查的公开内容。",
    statuses: [
      "DRAFT",
      "PENDING_PRIVACY_REVIEW",
      "PENDING_RELEASE",
      "SCHEDULED",
      "PUBLISHED",
      "SUPERSEDED",
      "WITHDRAWN"
    ] as ContentStatus[]
  }
};
export type ChannelOption = { value: string; label: string };
export function nextStep(kind: ContentKind, status: ContentStatus) {
  if (status === "DRAFT")
    return kind === "notice"
      ? "核对正文和接收范围后送审，由另一位有放行职责的同事在手机端放行。"
      : "核对公开内容后送保密与公开性审查，审查通过后再由第三位同事放行。";
  if (status === "PENDING_PRIVACY_REVIEW")
    return "等待另一位同事在手机端完成保密与公开性审查。需修改时，由审查人退回起草。";
  if (status === "PENDING_RELEASE")
    return kind === "notice"
      ? "等待另一位同事在手机端放行。接收人数在放行时确定；当前尚未发布。"
      : "保密与公开性审查已通过，等待第三位同事在手机端放行；当前尚未公开。";
  if (status === "RELEASED")
    return "通知已发布。接收人数来自发布时的名单，不代表已读或已确认人数。";
  if (status === "SCHEDULED") return "内容已获放行，将在计划时间公开；可按流程撤回定时。";
  if (status === "SUPERSEDED") return "此稿已由纠错版本取代，原稿与办理记录继续保留。";
  if (status === "PUBLISHED") return "内容已通过审查并公开。后续撤回按既有办理流程处理。";
  return status === "WITHDRAWN"
    ? kind === "notice" ? "通知已撤回，办理记录和发布时的接收名单继续保留。" : "内容已撤下，办理记录继续保留。"
    : "通知草稿已作废，办理记录继续保留。";
}
export const paragraphs = (value: string) =>
  value
    .split(/\n\s*\n/)
    .map((row) => row.trim())
    .filter(Boolean);
